import { useState, useEffect, useCallback } from 'react';
import { settingsAPI } from '../config/api';
import { getDefaultZones } from '../utils/shippingZones';

const defaultSettings = {
  zones: getDefaultZones(),
  fixed_shipping_enabled: false,
  updated_at: null,
};

const normalizeSettings = (data) => {
  if (!data || typeof data !== 'object') {
    return defaultSettings;
  }

  return {
    zones: {
      ...getDefaultZones(),
      ...(data.zones || {}),
    },
    fixed_shipping_enabled: data.fixed_shipping_enabled ?? false,
    updated_at: data.updated_at ?? null,
  };
};

let cachedSettings = null;
let pendingPromise = null;

export const updateShippingSettingsCache = (data) => {
  cachedSettings = normalizeSettings(data);
};

export const clearShippingSettingsCache = () => {
  cachedSettings = null;
};

export const useShippingSettings = () => {
  const [settings, setSettings] = useState(cachedSettings || defaultSettings);
  const [loading, setLoading] = useState(!cachedSettings);
  const [error, setError] = useState(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await settingsAPI.getShipping();
      const normalized = normalizeSettings(data);
      cachedSettings = normalized;
      setSettings(normalized);
    } catch (err) {
      setError(err.message || 'Impossibile caricare le impostazioni di spedizione');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (cachedSettings) {
      setSettings(cachedSettings);
      setLoading(false);
      return;
    }

    if (!pendingPromise) {
      pendingPromise = settingsAPI
        .getShipping()
        .then((data) => {
          const normalized = normalizeSettings(data);
          cachedSettings = normalized;
          return normalized;
        })
        .finally(() => {
          pendingPromise = null;
        });
    }

    let isMounted = true;

    pendingPromise
      .then((data) => {
        if (isMounted) {
          setSettings(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(err.message || 'Impossibile caricare le impostazioni di spedizione');
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return {
    settings: settings || defaultSettings,
    loading,
    error,
    refresh: fetchSettings,
  };
};

